<template>
    <PageSection fixed>
        <PageHeader title="Courses">
            <button id="download-btn" class="btn-primary mr-5"
                :class="[downloading && 'flex items-center gap-4 transition']" @click="downloadCourseData()"
                :disabled="downloading" download>
                Download Course Data
                <LoadingSpinner v-if="downloading" class="text-white" />
            </button>
        </PageHeader>
        <Card>
            <TableLayout>
                <template #toolbar>
                    <div class="table-toolbar b-0">
                        <div class="flex grow">
                            <div class="flex-row">
                                <BulkSelectToolbar :table="table">
                                    <button v-if="hasAdoptionsNeeded(selectedCourses)" @click="
                                        openUpdateAdoptionsNeededDialog()
                                        " class="btn-table-action">
                                        No adoptions needed
                                    </button>
                                </BulkSelectToolbar>
                                <SearchBar placeholder="Search for courses" @search="fetch({ query: $event })"
                                    :initial-search="searchQuery" />
                                <FilterSet :filters="filterableColumns" @filters-updated="handleUpdatedFilters"
                                    :initialFilters="initialFilters" />
                                <DateFilterComponent :selectedDateAfter="selectedDateAfter"
                                    :selectedDateRange="selectedDateRange" v-model:multi-calendars="isMultiCalendars"
                                    @update:selectedDateAfter="handleDateRangeChanged"
                                    @update:selectedDateRange="handleDateRangeChanged" />
								<ColumnVisibility :table="table" />
                            </div>
                        </div>
                    </div>
                </template>
                <Table :table="table" :show-loading-spinner="loading" aria-describedby="page-header" class="b-l-style-none b-r-style-none b-b-style-solid">
                    <thead class="table-thead">
                        <tr class="flex" v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                            <TableHeaderCell v-for="header in headerGroup.headers" :header="header">
                                <template #selector>
                                    <RowSelector :row="table"></RowSelector>
                                </template>
                            </TableHeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="table-row" v-for="row in rows" :key="row.id">
                            <TableDataCell v-for="cell in row.getVisibleCells()" :cell="cell">
                                <template #selector>
                                    <RowSelector :row="row"></RowSelector>
                                </template>
    
                                <template #status>
                                    <Tag :variant="getStatusChipType(cell.row.original)" :clearable="false">
                                        {{ cell.row.original.status
                                        }}
                                    </Tag>
                                </template>
    
                                <template #actions>
                                    <Menu placement="auto" :items="getRowActions(cell)" class="cell-menu" is-icon />
                                </template>
    
                            </TableDataCell>
                        </tr>
                    </tbody>
                    <template #empty>
                        <EmptyState :icon="message.icon" :title="message.title" :text="message.description">
                        </EmptyState>
                    </template>
                </Table>
    
                <template #paginator>
                    <Paginator :table="table" :page-sizes="[5, 10, 25, 50, 100, 200]" class="b-0" />
                </template>
            </TableLayout>
        </Card>
        <ModalDialog :show="showConfirmDialog" title="Confirming No Adoptions Needed" size="small" @closeModal="cancel"
            continueButtonText="Continue" :continueAction="confirm" :loading-continue-button="saving">
            <p class="mb-4">Are you sure?</p>
            <p>
                By selecting "No adoptions needed" you are confirming students will
                not receive any materials provided by BibliU for
                {{ selectedCourseCodesMsg }}.
            </p>
        </ModalDialog>
        <ModalDialog :show="showConfirmCoursesWithAdoptions" title="Confirming No Adoptions Needed" size="small"
            @closeModal="cancel" continueButtonText="Continue" :continueAction="confirm"
            :loading-continue-button="saving">
            <p class="mb-4">Are you sure?</p>
            <p class="mb-4">
                This course already has an adoption submitted, by selecting "No
                adoptions needed" you are confirming students will not receive any
                materials provided by BibliU for {{ selectedCoursesWithAdoptionsMsg }}
            </p>
            <p>
                By selecting "No adoptions needed" you are confirming students will
                not receive any materials provided by BibliU for
                {{ selectedCourseCodesMsg }}.
            </p>
        </ModalDialog>
		<ModalDialog :show="showConfirmNoAdoptionsNeeded" title="Confirming Undo 'No Adoptions Needed'" size="small" @closeModal="cancel"
            continueButtonText="Continue" :continueAction="confirm" :loading-continue-button="saving">
            <p class="mb-4">Are you sure?</p>
            <p>
                This will revert the following courses to the state they were in before being marked as "No adoptions needed": 
                {{ selectedCourseCodesMsg }}.
            </p>
        </ModalDialog>
        <SuggestionDialog v-if="isSelecting || isFetching || isSaving || isRejecting" @cancel="closeSelectionDialog"
            @accept="confirmSuggestions" @reject="rejectSuggestions" :suggested-adoptions="getSuggestedAdoptions"
            @clearSelections="clearSelections"
            :is-rejecting="isRejecting" :is-saving="isSaving" :is-fetching="isFetching"
            @toggle-book-selection="toggleBookSelection" :title="getTitle" />
    </PageSection>
</template>
<script lang="ts" setup>
import {
	Cell,
	functionalUpdate,
	getCoreRowModel,
	useVueTable,
} from "@tanstack/vue-table";

import { match } from "ts-pattern";
import { computed, ref, watch } from "vue";
import { definePage } from "vue-router/auto";
import { useGuardedTableColumns } from "../../../../shared/components/ComposableTable/useGuardedTableColumns";
import {
	Filter,
	SelectedFilter,
} from "../../../../shared/components/Filters/Filter.vue";
import { MenuItemData } from "../../../../shared/components/Menu.vue";
import { TagVariants } from "../../../../shared/components/Tag.vue";
import { useFeatureFlag } from "../../../../shared/composables/useFeatureFlag";
import SuggestionDialog from "../../components/Courses/SuggestionsDialog.vue";
import { useAdoptionSuggestion } from "../../composables/useAdoptionSuggestion";
import { useCourseTable } from "../../composables/useCourseTable";
import { useInstitution } from "../../composables/useInstitution";
import { usePermission } from "../../composables/usePermission";
import {
	type Context,
	CourseRow,
	type CourseStatus,
	type Courses,
	formatCourseCodes,
} from "../../state-machines/courses-table-actor";
import { useAcademicDates } from "../../stores/academic-dates";
import { trpc } from "../../trpc/client";
import { generateTableColumns } from "../../utils/booklist-table-columns";
import { getRequestPageRoute } from "../../utils/request-page-route";

const { institutionConfig, isEAorIA } = useInstitution();
const { hasPermission } = usePermission();

const windowLanguage = window.navigator.language;

const academicDatesStore = useAcademicDates();

const additionalSectionsEnabled =
	institutionConfig?.value.additional_sections === "prompted";

const newRequestLinkEnabled = hasPermission(
	institutionConfig?.value.adminUserPermissions,
	"booklist:request",
);

const {
	columns: courseTableColumns,
	rows: data,
	totalRowCount,
	pagination,
	loading,
	columnPinning,
	message,
	showConfirmDialog,
	showConfirmNoAdoptionsNeeded,
	rowSelection,
	selectedCourses,
	showConfirmCoursesWithAdoptions,
	selectedCoursesWithAdoptions,
	downloading,
	saving,
	searchQuery,
	cancel,
	confirm,
	fetch,
	download,
	selectRows,
	updateAdoptionNeeded,
} = useCourseTable();

const {
	openSelectionDialog,
	closeSelectionDialog,
	toggleBookSelection,
	acceptSuggestions,
	rejectSuggestions,
	clearSelections,
	getSuggestedAdoptions,
	getTitle,
	isSelecting,
	isFetching,
	isSaving,
	isRejecting,
} = useAdoptionSuggestion();

function confirmSuggestions() {
	const selectedSuggestions = getSuggestedAdoptions.value.filter(
		(suggestion) => suggestion.selected,
	);
	acceptSuggestions(selectedSuggestions);
}

const rows = computed(() => table.getCoreRowModel().rows);

// The columns we want to display in the table.
const columns = useGuardedTableColumns<CourseRow>((helper) => [
	{
		columnDef: helper.display({
			id: "selector",
			size: 48,
			enableSorting: false,
			enableResizing: false,
			enableHiding: false,
		}),
	},
	...generateTableColumns(courseTableColumns.value, helper),
	{
		columnDef: helper.display({
			id: "actions",
			header: "Actions",
			size: 72,
			enableSorting: false,
			enableResizing: false,
			enableHiding: false,
		}),
	},
]);

const table = useVueTable<CourseRow>({
	getCoreRowModel: getCoreRowModel<CourseRow>(),
	manualPagination: true,
	columnResizeMode: "onChange",
	getRowId: (row) => row.id,
	get data() {
		return data.value;
	},
	get columns() {
		return columns.value;
	},
	get rowCount() {
		return totalRowCount.value;
	},
	state: {
		get pagination() {
			return pagination.value;
		},
		get columnPinning() {
			return columnPinning.value;
		},
		get rowSelection() {
			return rowSelection.value;
		},
	},
	onPaginationChange(updater) {
		const newPagination = functionalUpdate(updater, pagination.value);

		const courseFilters = getCourseFilters();

		fetch({
			page: newPagination.pageIndex + 1,
			rowsPerPage: newPagination.pageSize,
			filters: parseFiltersToQuery(courseFilters),
		});

		window.localStorage.setItem(
			"preferredTablePageSize",
			newPagination.pageSize.toString(),
		);
	},
	onRowSelectionChange(updater) {
		const newRowSelection = functionalUpdate(updater, rowSelection.value);
		const data: Context["selectedCourses"] = [];
		const selectedCoursesMap = new Map<
			string,
			Context["selectedCourses"][number]
		>(
			selectedCourses.value.map((course) => [
				`${course.aggregated_course_code}::${course.path}`,
				course,
			]),
		);

		for (const [key, value] of Object.entries(newRowSelection)) {
			const item = rows.value.find((row) => row.id === key);

			if (selectedCoursesMap.has(key)) {
				data.push(
					selectedCoursesMap.get(key) as Context["selectedCourses"][number],
				);
				continue;
			}

			if (value) {
				data.push({
					aggregated_course_code: item?.original.item
						.aggregated_course_code as string,
					path: item?.original.item.path as string,
					hasAdoptions: Boolean(item?.original.adoptions),
					adoptions_needed: Boolean(item?.original.adoptions_needed),
				});
			}
		}
		selectRows(data);
	},
});

const initialFilters = getCourseFilters();

const filterableColumns = computed<Filter[]>(() => {
	const filterableFields: Record<string, string> = {
		"item.aggregated_course_code": "item.aggregated_course_code",
		"item.course_name": "item.course_name",
		"item.professor_full_name": "item.professor_full_name",
		"item.department": "item.department",
		"item.course_reference_number": "item.course_reference_number",
		status: "status",
	};

	const filteredColumns: Filter[] = [];

	const statusOptions: Filter["options"] = {
		search: "local",
		type: "multi",
		fetch: () => {
			return Promise.resolve({
				values: ["Not Needed", "Missing", "Suggested", "Submitted"],
				hasMore: false,
			});
		},
	};

	for (const column of columns.value) {
		const field =
			typeof column.id === "string" ? filterableFields[column.id] : null;

		if (!field) {
			continue;
		}

		filteredColumns.push({
			id: field,
			label: (column.header ?? field) as string,
			options:
				field === "status"
					? statusOptions
					: {
							search: "server",
							type: "multi",
							fetch: (query?: string) => {
								return trpc.courseManagement.getFilterValues.query({
									field,
									query,
								});
							},
						},
		});
	}

	return filteredColumns;
});

function parseFiltersToQuery(filters: SelectedFilter[]) {
	return filters.reduce(
		(acc, filter) => {
			const key = filter.column.replace("item.", "");
			acc[key] = (acc[key] || []).concat(filter.columnValue);
			return acc;
		},
		{} as Record<string, string[]>,
	);
}

async function handleUpdatedFilters(latestFilters: SelectedFilter[]) {
	const groupedFilters = parseFiltersToQuery(latestFilters);
	fetch({
		rowsPerPage: Number(window.localStorage?.preferredTablePageSize ?? 25),
		page: 1,
		startDate: academicDatesStore.selectedDates?.startDate || undefined,
		endDate: academicDatesStore.selectedDates?.endDate || undefined,
		filters: groupedFilters,
	});

	// Store the filters to localstorage
	window.localStorage.setItem("cousesFilters", JSON.stringify(latestFilters));
}

/**
 * Get filters from localstorage and parse it into an array
 */
function getCourseFilters(): SelectedFilter[] {
	try {
		const courseFilters = window.localStorage?.getItem("cousesFilters");
		return courseFilters ? JSON.parse(courseFilters) : [];
	} catch (error) {
		console.error(error);
		return [];
	}
}

const selectedCourseCodesMsg = computed(() =>
	formatCourseCodes(selectedCourses.value),
);
const selectedCoursesWithAdoptionsMsg = computed(() =>
	formatCourseCodes(selectedCoursesWithAdoptions.value),
);

const selectedDateAfter = ref<Date>(new Date());
const selectedDateRange = ref<Date[]>(
	academicDatesStore.selectedDates
		? [
				new Date(academicDatesStore.selectedDates?.startDate),
				new Date(academicDatesStore.selectedDates?.endDate),
			]
		: [],
);
const isMultiCalendars = ref<boolean>(true);

watch(
	() => academicDatesStore.selectedDates,
	(newValue, oldValue) => {
		if (newValue?.id === oldValue?.id) {
			return;
		}

		const courseFilters = getCourseFilters();

		fetch({
			rowsPerPage: Number(window.localStorage?.preferredTablePageSize ?? 25),
			page: 1,
			startDate: academicDatesStore.selectedDates?.startDate || undefined,
			endDate: academicDatesStore.selectedDates?.endDate || undefined,
			filters: parseFiltersToQuery(courseFilters),
		});

		// update date picker and switch to multi calendars
		isMultiCalendars.value = true;
		selectedDateRange.value = academicDatesStore.selectedDates
			? [
					new Date(academicDatesStore.selectedDates?.startDate),
					new Date(academicDatesStore.selectedDates?.endDate),
				]
			: [];
	},
	{
		immediate: true,
	},
);

function handleDateRangeChanged([startDate, endDate]: [Date?, Date?]) {
	const courseFilters = getCourseFilters();

	fetch({
		rowsPerPage: Number(window.localStorage?.preferredTablePageSize ?? 25),
		page: 1,
		startDate: startDate?.toISOString(),
		endDate: endDate?.toISOString(),
		filters: parseFiltersToQuery(courseFilters),
	});
}

function openUpdateAdoptionsNeededDialog(course?: CourseRow) {
	// Single course action
	if (course) {
		updateAdoptionNeeded({
			courses: [
				{
					aggregated_course_code: course?.item.aggregated_course_code,
					path: course.academic_date_path,
					hasAdoptions: course.adoptions > 0,
				},
			],
			adoptions_needed: false,
		});
	}
	// bulk courses action
	updateAdoptionNeeded({
		adoptions_needed: false,
	});
	table.resetRowSelection();
}

function undoNoAdoptionsNeeded(course?: CourseRow) {
	// Single course action
	if (course) {
		updateAdoptionNeeded({
			courses: [
				{
					aggregated_course_code: course?.item.aggregated_course_code,
					path: course.academic_date_path,
					hasAdoptions: course.adoptions > 0,
				},
			],
			adoptions_needed: true,
		});
	}
	// bulk courses action
	updateAdoptionNeeded({
		adoptions_needed: true,
	});
	table.resetRowSelection();
}

function goToNewRequest(course: CourseRow) {
	if (!institutionConfig.value || !newRequestLinkEnabled) {
		return;
	}
	const courseCode = `${course.item.start_date}::${course.item.aggregated_course_code}`;
	const queryParams = new URLSearchParams({
		courseId: courseCode,
	});

	const route = getRequestPageRoute(
		institutionConfig.value.approval_config,
		isEAorIA.value,
	);

	globalThis.window.location.href = `${route}?${queryParams.toString()}`;
}

function getStatusChipType(course: CourseRow) {
	const status = course.status;
	return match<CourseStatus, TagVariants>(status)
		.with("Missing", () => "negative")
		.with("Submitted", () => "positive")
		.with("Not Needed", () => "misc")
		.with("Suggested", () => "action")
		.otherwise(() => "positive");
}

function hasAdoptionsNeeded(courses: Context["selectedCourses"]) {
	return courses?.some((course) => course.adoptions_needed);
}

function downloadCourseData() {
	download(windowLanguage);
}

function getRowActions(cell: Cell<Courses[number], unknown>): MenuItemData[] {
	const actions = [];
	const adoptionStatus = cell.row.original.status;

	if (adoptionStatus !== "Not Needed") {
		actions.push({
			label: "No adoption needed",
			action: () => {
				openUpdateAdoptionsNeededDialog(cell.row.original);
			},
		});

		if (adoptionStatus === "Missing") {
			actions.push({
				label: "New request",
				action: () => {
					goToNewRequest(cell.row.original);
				},
				disabled: !newRequestLinkEnabled,
			});
		}
	} else if (adoptionStatus === "Not Needed") {
		actions.push({
			label: "Undo 'No adoption needed'",
			action: () => {
				undoNoAdoptionsNeeded(cell.row.original);
			},
		});
	}

	if (additionalSectionsEnabled && adoptionStatus === "Suggested") {
		const {
			department_code,
			course_code,
			path,
			aggregated_course_code,
			department,
		} = cell.row.original.item;
		const title = `${department} — ${aggregated_course_code}`;
		actions.push({
			label: "Review Suggestions",
			action: () => {
				openSelectionDialog(
					{
						department_code,
						course_code,
						path,
					},
					title,
					aggregated_course_code,
					cell.row.original.adoptions_needed,
					cell.row.original.item,
				);
			},
		});
	}
	return actions;
}

definePage({
	meta: {
		requiresAuth: true,
		permission: "adoptions:viewAllCourses",
	},
});
</script>
